/*
 * File: CurrencyInput.tsx
 * Author: Austin Robinson
 * Date Created: 7/14/2022
 * Copyright (c) DrivingDoc 2022
 */
import { useEffect, useMemo, useRef, useState } from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { keepPreviousData, QueryClientProvider, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';
import { isAxiosError } from 'axios';
import moment from 'moment';

import { ErrorResponse } from 'api';
import {
  enrollStudentInPeriodMutation,
  enrollStudentInSectionMutation,
  getCoursePeriodOptions,
  getCoursePeriodQueryKey,
  listCoursePeriodsQueryKey,
  searchSectionEnrollmentsOptions,
} from '@drivingdoc/api-client/generated/react-query';
import { ListCoursePeriodsData } from '@drivingdoc/api-client/generated/types';
import { Options } from '@drivingdoc/api-client/generated/sdk';
import { CoursePeriod } from 'types/periods';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

// interface Section {
//   'tenant_id': string,
//   'id': string,
//   'course_id': string,
//   'title': string,
//   'description': string | null,
//   'open_enrollment': string | null,
//   'close_enrollment': string | null,
//   'capacity': number | null,
//   'tenant_location_id': string | null
//   'course': {
//     'tenant_id': string,
//     'id': string,
//     'course_type': 'behind_the_wheel' | 'in_person' | 'synchronous_online' | 'fully_online',
//     'schedule_type': 'self_scheduled' | 'fixed' | 'manual' | 'no_schedule',
//     'title': string,
//     'description': string | null,
//     'created_at' : string | null,
//     'created_by' : string | null,
//     'updated_at' : string | null,
//     'updated_by' : string | null
//   }
// }

// interface UpdateFilterParams {
//   fName?: string;
//   lName?: string;
// }

export default function usePeriodEnrollmentDialog() {
  // temp fix for scroll to top bug in chrome
  const initialScrollPosition = useRef<number | null>(null);

  // LicenseInfo.setLicenseKey(
  //   '0bc1f2c598450714f08031134df5353fT1JERVI6NDEwNjEsRVhQSVJZPTE2ODA2MjIyOTEwMDAsS0VZVkVSU0lPTj0x',
  // );

  // Dialog State
  const [open, setOpen] = useState<boolean>(false);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [coursePeriodId, setCoursePeriodId] = useState<string | undefined>(undefined);
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  // const [appSyncFilter, setAppSyncFilter] = useState<any>({});
  const doneSelectingCallback = useRef<((students: string[]) => void) | null>(null);
  const hasFocused = useRef(false);
  const queryClient = useQueryClient();
  const dialogs = useDialogs();

  const coursePeriodQuery = useQuery({
    ...getCoursePeriodOptions({
      path: {
        id: coursePeriodId ?? '',
      },
    }),
    enabled: !!coursePeriodId,
  });
  const coursePeriod = coursePeriodQuery.data as CoursePeriod | undefined;

  // Data Grid State
  // const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [selectedStudents, setSelectedStudents] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  });
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [currentPageSize, setCurrentPageSize] = useState<number>(100);
  // const [rowCount, setRowCount] = useState<number>(0);
  // const [error, setError] = useState<ErrorOverlayProps>();
  const columns: GridColDef[] = [
    {
      field: 'last_name',
      headerName: 'Last',
      type: 'string',
      width: 130,
      sortable: false,
    },
    {
      field: 'first_name',
      headerName: 'First',
      type: 'string',
      width: 130,
      sortable: false,
    },
    {
      field: 'middle_name',
      headerName: 'Middle',
      type: 'string',
      width: 130,
      sortable: false,
    },
    {
      field: 'date_of_birth',
      headerName: 'DOB',
      type: 'date',
      width: 130,
      sortable: false,
      valueFormatter: (value) => moment(value as string).format('MM/DD/YYYY'),
    },
    {
      field: 'active',
      headerName: 'Status',
      type: 'boolean',
      sortable: false,
      renderCell: (params) => (
        <Chip
          sx={{ width: '100%!important' }}
          size="small"
          label={params.value ? 'Active' : 'Inactive'}
          // color={params.value ? 'success' : 'error'}
          color={params.value ? 'success' : 'error'}
          // variant="outlined"
        />
      ),
    },
  ];

  const { data, isFetching } = useQuery({
    ...searchSectionEnrollmentsOptions({
      body: {
        courseSectionId: showAll ? undefined : coursePeriod?.course_section.id,
        firstName: firstName,
        lastName: lastName,
        page: currentPage,
        pageSize: currentPageSize,
        sort: '-active,last_name,first_name,middle_name',
      },
    }),
    enabled: showAll || coursePeriod !== undefined,
    placeholderData: keepPreviousData,
  });
  const enrollmentRows = (data as { data: Array<Record<string, unknown>>; total: number } | undefined)?.data ?? [];
  const enrollmentCount = (data as { data: Array<Record<string, unknown>>; total: number } | undefined)?.total ?? 0;

  const enrollStudentInPeriod = useMutation(enrollStudentInPeriodMutation());
  const enrollStudentInSection = useMutation(enrollStudentInSectionMutation());

  // Reset to first page when search criteria change
  useEffect(() => {
    setCurrentPage(0);
  }, [firstName, lastName, showAll]);

  const resetState = () => {
    // Reset Dialog State
    // setMaxSelections(0); // remove seletion limit
    // setSelectedStudents([]); // clear selected students
    setSelectedStudents({
      type: 'include',
      ids: new Set(),
    }); // clear selected students
    setFirstName(''); // clear search field
    setLastName(''); // clear search field
    // setAppSyncFilter({});
    doneSelectingCallback.current = null;
    // Reset Data Grid State
    setCurrentPage(0);
    setCurrentPageSize(100);
  };

  const handleClose = () => {
    setOpen(false); // close the dialog
    setTimeout(() => resetState(), 100); // reset after dialog is closed
    setTimeout(() => {
      // prevent chrome bug that scrolls page to top
      if (initialScrollPosition.current !== null) {
        window.scrollTo(0, initialScrollPosition.current);
        initialScrollPosition.current = null;
      }
    }, 0);
  };

  const enrollInPeriod = async (overrideRestrictions = false): Promise<void> => {
    console.log('Enroll Student In Period');
    if (!coursePeriodId) return;

    try {
      await enrollStudentInPeriod.mutateAsync({
        body: {
          coursePeriodId: coursePeriodId,
          // studentId: selectedStudents[0],
          studentId: Array.from(selectedStudents.ids)[0] as string,
          overrideRestrictions: overrideRestrictions,
          enrollmentType: 'performance_and_observation',
        },
      });

      queryClient.invalidateQueries({
        queryKey: listCoursePeriodsQueryKey({} as Options<ListCoursePeriodsData>),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: getCoursePeriodQueryKey({ path: { id: coursePeriodId } }),
      });
      handleClose();
      dialogs.open(FeedbackDialog, {
        title: 'Registered!',
        icon: 'success',
      });
    } catch (error) {
      // if the error is not an axios error
      if (!isAxiosError<ErrorResponse>(error)) {
        dialogs.open(FeedbackDialog, {
          title: 'Error ',
          message: getApiErrorMessage(error),
        });
        return;
      }

      // if student is not enrolled in the course section
      if (error.response?.data?.error?.code === 'STUDENT_NOT_ENROLLED_IN_COURSE') {
        // show confirmation dialog
        const confirmed = await dialogs.confirm(
          `Would you like to manually enroll student in the section "${coursePeriod?.course_section.title}"?`,
          {
            title: 'Student Not Enrolled In Section',
            okText: 'Enroll',
            cancelText: 'Cancel',
            severity: 'error',
          },
        );

        // if user cancels, return
        if (!confirmed) {
          return;
        }

        try {
          // try to enroll student in course section
          await enrollStudentInSection.mutateAsync({
            body: {
              courseSectionId: coursePeriod?.course_section_id ?? '',
              studentId: Array.from(selectedStudents.ids)[0] as string,
            },
          });

          // retry enrolling student in period
          await enrollInPeriod(false);
        } catch (error) {
          dialogs.open(FeedbackDialog, {
            title: 'Error ',
            message: getApiErrorMessage(error),
          });
          return;
        }
      }

      // if the HTTP status code is 409 or 412
      if (error.response?.status && [409, 412].includes(error.response?.status)) {
        // show confirmation dialog
        const confirmed = await dialogs.confirm(
          `${error.response?.data?.error?.message} Would you like to override these restrictions and enroll the student anyway?`,
          {
            title: 'Self Schedule Policy Violation',
            okText: 'Override',
            cancelText: 'Cancel',
            severity: 'error',
          },
        );

        // if user cancels, return
        if (!confirmed) {
          return;
        }

        // otherwise enroll student in period with override
        await enrollInPeriod(true);
      }
    }
  };

  const openPeriodEnrollmentDialog = (id: string) => {
    setCoursePeriodId(id);
    setShowAll(false);
    // temp fix for scroll to top bug in chrome
    initialScrollPosition.current = document.documentElement.scrollTop;
    // doneSelectingCallback.current = onDoneSelecting;
    hasFocused.current = false;
    // setMaxSelections(max); // set max selection limit
    setOpen(true); // open the dialog
  };

  const getTitle = () => {
    if (coursePeriod?.capacity === 0 || coursePeriod?.capacity === null) {
      return 'Search For Students';
    }
    if (coursePeriod?.capacity === 1) {
      return 'Search For A Student';
    }
    return `Search For Up To ${coursePeriod?.capacity} Students (need to add subtraction)`;
  };

  // const updateFilter = _.debounce(({ fName, lName }: UpdateFilterParams) => {
  //   if (_.isNil(fName) && _.isNil(lName)) {
  //     return;
  //   }
  //   const filter = {
  //     and: {
  //       firstName: {
  //         wildcard: `${_.isNil(fName) ? firstName : fName}*`,
  //       },
  //       lastName: {
  //         wildcard: `${_.isNil(lName) ? lastName : lName}*`,
  //       },
  //     },
  //   };
  //   setAppSyncFilter(filter);
  // }, 100);

  const PeriodEnrollmentDialog = useMemo(
    () => (
      <QueryClientProvider client={queryClient}>
        <Dialog fullWidth maxWidth="md" open={open} onClose={handleClose}>
          <DialogTitle>{getTitle()}</DialogTitle>

          <DialogContent>
            <Grid container direction="column">
              <Grid container direction="column">
                <TextField
                  variant="standard"
                  value={firstName}
                  autoComplete="off"
                  onChange={(event) => {
                    setFirstName(event.target.value);
                    // updateFilter({ fName: event.target.value });
                  }}
                  label="First Name"
                  sx={{ pb: 1 }}
                  InputProps={{
                    startAdornment: <SearchIcon fontSize="small" />,
                    endAdornment: (
                      <IconButton
                        title="Clear"
                        aria-label="Clear"
                        size="small"
                        style={{ visibility: firstName ? 'visible' : 'hidden' }}
                        onClick={() => {
                          // updateFilter({ fName: '' });
                          setFirstName('');
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    ),
                  }}
                />
                <TextField
                  inputRef={(instance) => {
                    // autoFocus field when dialog opens
                    if (!hasFocused.current) {
                      hasFocused.current = true;
                      instance?.focus();
                    }
                  }}
                  variant="standard"
                  value={lastName}
                  autoComplete="off"
                  onChange={(event) => {
                    // updateFilter({ lName: event.target.value });
                    setLastName(event.target.value);
                  }}
                  label="Last Name"
                  InputProps={{
                    startAdornment: <SearchIcon fontSize="small" />,
                    endAdornment: (
                      <IconButton
                        title="Clear"
                        aria-label="Clear"
                        size="small"
                        style={{ visibility: lastName ? 'visible' : 'hidden' }}
                        onClick={() => {
                          // updateFilter({ lName: '' });
                          setLastName('');
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    ),
                  }}
                />
              </Grid>
              <Grid>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!showAll}
                      onChange={(event) => {
                        setShowAll(!event.target.checked);
                        // setSelectedStudents([]);
                        setSelectedStudents({
                          type: 'include',
                          ids: new Set(),
                        });
                      }}
                    />
                  }
                  label={`Only Show Students Enrolled In "${coursePeriod?.course_section.title}"`}
                />
              </Grid>
              <Grid container pt="8px" height="60vh">
                {data && (
                  <DataGrid
                    rows={enrollmentRows}
                    columns={columns}
                    // rowsPerPageOptions={[100]}
                    // getRowId={(row) => row.username}
                    rowCount={enrollmentCount}
                    // pageSize={currentPageSize}
                    loading={isFetching}
                    // rowSelectionModel={selectedStudents}
                    rowSelectionModel={selectedStudents}
                    checkboxSelection={coursePeriod?.capacity !== 1}
                    disableMultipleRowSelection
                    keepNonExistentRowsSelected
                    disableColumnMenu
                    density="compact"
                    filterMode="server"
                    pagination
                    paginationMode="server"
                    // error={error}
                    // onPageChange={(page) => {
                    //   setCurrentPage(page);
                    // }}
                    // onPageSizeChange={(pageSize: number) => {
                    //   setCurrentPageSize(pageSize);
                    // }}
                    onPaginationModelChange={(model: GridPaginationModel) => {
                      setCurrentPage(model.page);
                      setCurrentPageSize(model.pageSize);
                    }}
                    // onRowSelectionModelChange={(selectionModel: GridRowSelectionModel) => {
                    //   // const selectedStudents = data.filter((student: any) => selectionModel.indexOf(student.username) !== -1);
                    //   // selectedStudents.current = selectionModel as string[];
                    //   // onSelectStudent(selectedStudents);
                    //   if (coursePeriod?.capacity === null || selectionModel.length <= coursePeriod?.capacity) {
                    //     setSelectedStudents(selectionModel as string[]);
                    //   }
                    // }}

                    onRowSelectionModelChange={(newRowSelectionModel) => {
                      setSelectedStudents(newRowSelectionModel);
                    }}
                    // components={{
                    //   BaseSwitch: Checkbox,
                    //   LoadingOverlay: LinearProgress,
                    // }}
                    sx={{
                      '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
                        outline: 'none',
                      },
                      // '& .css-1ui3wbn-MuiInputBase-root-MuiTablePagination-select': {
                      '& .MuiInputBase-root': {
                        width: 'auto!important',
                      },
                      '& .MuiTablePagination-select': {
                        textAlign: 'center',
                        textAlignLast: 'center',
                      },
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button disabled={selectedStudents.ids.size === 0} onClick={() => enrollInPeriod(false)}>
              Ok
            </Button>
          </DialogActions>
        </Dialog>
      </QueryClientProvider>
    ),
    [
      // appSyncFilter,
      currentPage,
      currentPageSize,
      data,
      enrollmentRows,
      enrollmentCount,
      firstName,
      hasFocused,
      isFetching,
      lastName,
      open,
      selectedStudents,
      showAll,
    ],
  );

  return {
    openPeriodEnrollmentDialog,
    PeriodEnrollmentDialog,
  };
}
